/*
  CASOPRA HOMEPAGE LAZY SECTIONS
  VERSION: V7 EDITORIAL POOL PAGE AWARE
  CHANGELOG:
    1. Keeps the stable V5/V6-style timing and does not touch the header.
    2. Adds support for sections with data-casopra-pool-page-aware="true".
    3. For Editorial Products, computes the current rotation bucket before fetch.
    4. Adds the correct page parameter to the first Section Rendering API request.
    5. Goal: avoid rendering pool page 1 first and swapping products after load.
*/
(function() {
  'use strict';

  var SELECTOR = '[data-casopra-homepage-lazy-section]';

  var started = false;
  var observer = null;
  var loading = Object.create(null);
  var loaded = Object.create(null);
  var queued = Object.create(null);
  var queue = [];
  var processingQueue = false;
  var firstIntentCaptured = false;
  var scanTicking = false;

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function getSectionId(shell) {
    return shell && shell.getAttribute('data-casopra-section-id');
  }

  function getSectionType(shell) {
    return (shell && shell.getAttribute('data-casopra-section-type') || '').toLowerCase();
  }

  function isHeavyShell(shell) {
    var type = getSectionType(shell);
    return type === 'editorial-products' ||
      type === 'editorial-product' ||
      (shell && shell.classList && shell.classList.contains('casopra-homepage-lazy-shell--editorial-products'));
  }

  function getShells() {
    return toArray(document.querySelectorAll(SELECTOR)).filter(function(shell) {
      var sectionId = getSectionId(shell);
      return shell && sectionId && !loaded[sectionId] && !loading[sectionId] && !queued[sectionId];
    });
  }

  function hashString(value) {
    var hash = 2166136261;
    value = String(value || '');

    for (var i = 0; i < value.length; i += 1) {
      hash ^= value.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }

    return hash >>> 0;
  }

  function gcd(a, b) {
    a = Math.abs(a);
    b = Math.abs(b);

    while (b) {
      var temp = b;
      b = a % b;
      a = temp;
    }

    return a || 1;
  }

  function getCoprimeStep(seed, pageCount) {
    if (pageCount <= 1) return 1;

    var step = (seed % pageCount) + 1;
    var guard = 0;

    while (gcd(step, pageCount) !== 1 && guard < pageCount + 2) {
      step += 1;
      if (step > pageCount) step = 1;
      guard += 1;
    }

    return step || 1;
  }

  function getBucket(rotationInterval) {
    if (!rotationInterval || rotationInterval <= 0) return 0;
    return Math.floor(Date.now() / 1000 / rotationInterval);
  }

  function getTargetPoolPageForShell(shell, sectionId) {
    if (!shell || shell.getAttribute('data-casopra-pool-page-aware') !== 'true') return null;

    var pageCount = parseInt(shell.getAttribute('data-casopra-total-pool-pages'), 10) || 1;
    var rotationInterval = parseInt(shell.getAttribute('data-casopra-rotation-interval'), 10) || 0;

    if (pageCount <= 1 || rotationInterval <= 0) return 1;

    var hashSectionId = shell.getAttribute('data-casopra-hash-section-id') || ('shopify-section-' + sectionId);
    var collectionKey = shell.getAttribute('data-casopra-collection-key') || sectionId;
    var bucket = getBucket(rotationInterval);
    var base = hashSectionId + ':' + collectionKey + ':' + pageCount;
    var start = hashString(base + ':start') % pageCount;
    var step = getCoprimeStep(hashString(base + ':step'), pageCount);
    var targetIndex = (start + (bucket * step)) % pageCount;

    return targetIndex + 1;
  }

  function sectionUrl(sectionId, shell) {
    var url = new URL(window.location.href);
    var targetPoolPage = getTargetPoolPageForShell(shell, sectionId);

    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    url.searchParams.set('sections', sectionId);

    if (targetPoolPage) {
      url.searchParams.set('page', String(targetPoolPage));
      url.searchParams.set('_casopra_pool_page', String(targetPoolPage));
      url.searchParams.set('_casopra_pool_bucket', String(getBucket(parseInt(shell.getAttribute('data-casopra-rotation-interval'), 10) || 0)));
    }

    return url.pathname + '?' + url.searchParams.toString();
  }

  function copyScriptAttributes(from, to) {
    to.async = false;
    to.defer = false;

    toArray(from.attributes).forEach(function(attr) {
      to.setAttribute(attr.name, attr.value);
    });

    if (!to.type && from.type) {
      to.type = from.type;
    }
  }

  function runScripts(root) {
    if (!root || !root.querySelectorAll) return;

    toArray(root.querySelectorAll('script')).forEach(function(oldScript) {
      var newScript = document.createElement('script');
      copyScriptAttributes(oldScript, newScript);

      if (oldScript.src) {
        newScript.src = oldScript.src;
      } else {
        newScript.text = oldScript.textContent || '';
      }

      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  function wakeSectionOnly(root, sectionId) {
    try {
      if (window.CasopraGridProductWakeDeferredImages) {
        window.CasopraGridProductWakeDeferredImages(root);
      }
    } catch (e) {}

    try {
      document.dispatchEvent(new CustomEvent('casopra:homepage-section-loaded', {
        detail: {
          sectionId: sectionId,
          section: root
        }
      }));
    } catch (e) {}
  }

  function extractSectionHtml(payload, sectionId) {
    if (!payload) return '';
    if (typeof payload === 'string') return payload;

    if (payload[sectionId]) return payload[sectionId];

    var keys = Object.keys(payload);
    if (keys.length) return payload[keys[0]] || '';

    return '';
  }

  function normalizeReturnedSection(html, sectionId) {
    var template = document.createElement('template');
    template.innerHTML = (html || '').trim();

    if (!template.content || !template.content.childNodes.length) return null;

    var escapedId = window.CSS && CSS.escape
      ? CSS.escape(sectionId)
      : sectionId.replace(/("|'|\\|\.|\[|\]|\#|\:|\>|\+|\~|\*)/g, '\\$1');

    var section = template.content.querySelector('#shopify-section-' + escapedId);

    if (section) return section;

    var wrapper = document.createElement('div');
    wrapper.id = 'shopify-section-' + sectionId;
    wrapper.className = 'shopify-section homepage-lazy-wrapper';

    while (template.content.firstChild) {
      wrapper.appendChild(template.content.firstChild);
    }

    return wrapper;
  }

  function enqueueShell(shell, reason) {
    if (!shell || !shell.parentNode) return;

    var sectionId = getSectionId(shell);

    if (!sectionId || loading[sectionId] || loaded[sectionId] || queued[sectionId]) return;

    queued[sectionId] = true;
    shell.setAttribute('data-casopra-lazy-queued', reason || 'true');

    queue.push(shell);
    queue.sort(function(a, b) {
      var aTop = a.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0);
      var bTop = b.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0);

      return aTop - bTop;
    });

    processQueue();
  }

  function processQueue() {
    if (processingQueue) return;
    if (!queue.length) return;

    processingQueue = true;

    var shell = queue.shift();
    var sectionId = getSectionId(shell);

    if (sectionId) {
      delete queued[sectionId];
    }

    loadShell(shell).then(function() {
      processingQueue = false;

      /*
        Keep a tiny pause to avoid inserting several Shopify sections in the same frame.
        The pause is shorter than before so heavier product sections are ready sooner.
      */
      setTimeout(processQueue, 90);
    });
  }

  function loadShell(shell) {
    if (!shell || !shell.parentNode) {
      return Promise.resolve(null);
    }

    var sectionId = getSectionId(shell);

    if (!sectionId || loading[sectionId] || loaded[sectionId]) {
      return Promise.resolve(null);
    }

    loading[sectionId] = true;
    shell.classList.add('is-loading');

    return fetch(sectionUrl(sectionId, shell), {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Section fetch failed: ' + response.status);
        }

        return response.json();
      })
      .then(function(payload) {
        var html = extractSectionHtml(payload, sectionId);
        var newSection = normalizeReturnedSection(html, sectionId);

        if (!newSection) {
          throw new Error('Section HTML was empty for ' + sectionId);
        }

        newSection.setAttribute('data-casopra-homepage-lazy-loaded', 'true');

        shell.parentNode.replaceChild(newSection, shell);

        runScripts(newSection);
        wakeSectionOnly(newSection, sectionId);

        loaded[sectionId] = true;

        return newSection;
      })
      .catch(function(error) {
        shell.classList.remove('is-loading');
        shell.setAttribute('data-casopra-lazy-error', 'true');

        if (window.console && console.warn) {
          console.warn('[Casopra] Lazy homepage section failed:', sectionId, error);
        }

        return null;
      })
      .finally(function() {
        delete loading[sectionId];
      });
  }

  function getLookAheadMultiplier(shell) {
    var isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;

    /*
      Editorial Products is heavier because the returned section still has to execute
      its own product-pool script and clone product cards from templates.
      Load it earlier than simple image/text sections so the user does not land on a blank shell.
    */
    if (isHeavyShell(shell)) {
      return isMobile ? 3.0 : 3.8;
    }

    return isMobile ? 1.55 : 1.85;
  }

  function scanAndQueueShells() {
    var shells = getShells();
    if (!shells.length) return;

    var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 800;
    var currentY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    var candidates = [];

    shells.forEach(function(shell) {
      var rect = shell.getBoundingClientRect();
      var absoluteTop = rect.top + currentY;
      var lookAhead = viewportHeight * getLookAheadMultiplier(shell);

      if (absoluteTop <= currentY + lookAhead) {
        candidates.push({
          shell: shell,
          top: absoluteTop,
          heavy: isHeavyShell(shell)
        });
      }
    });

    if (!candidates.length) return;

    candidates.sort(function(a, b) {
      return a.top - b.top;
    });

    candidates.forEach(function(item) {
      enqueueShell(item.shell, item.heavy ? 'heavy-lookahead' : 'lookahead');
    });
  }

  function scheduleScan() {
    if (scanTicking) return;

    scanTicking = true;

    window.requestAnimationFrame(function() {
      scanTicking = false;
      scanAndQueueShells();
    });
  }

  function observeShells() {
    var shells = getShells();

    if (!shells.length) return;

    if (!('IntersectionObserver' in window)) {
      enqueueShell(shells[0], 'no-intersection-observer');
      return;
    }

    var isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;

    /*
      Slightly earlier than the previous version, but still gated behind user intent.
      The custom look-ahead scan does the heavier lifting for Editorial Products.
    */
    var rootMargin = isMobile ? '360px 0px' : '560px 0px';

    observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;

        var shell = entry.target;

        observer.unobserve(shell);
        enqueueShell(shell, isHeavyShell(shell) ? 'heavy-intersection' : 'intersection');
      });
    }, {
      rootMargin: rootMargin,
      threshold: 0.01
    });

    shells.forEach(function(shell) {
      observer.observe(shell);
    });
  }

  function warmHeavySections() {
    var shells = getShells();
    if (!shells.length) return;

    var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 800;
    var currentY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    shells.forEach(function(shell) {
      if (!isHeavyShell(shell)) return;

      var rect = shell.getBoundingClientRect();
      var absoluteTop = rect.top + currentY;

      if (absoluteTop <= currentY + viewportHeight * 4.8) {
        enqueueShell(shell, 'heavy-warmup');
      }
    });
  }

  function startLazyLoading() {
    if (started) return;

    started = true;

    observeShells();

    scheduleScan();

    setTimeout(scheduleScan, 120);
    setTimeout(warmHeavySections, 450);

    window.addEventListener('scroll', scheduleScan, { passive: true });
    window.addEventListener('resize', scheduleScan, { passive: true });
  }

  function captureIntent() {
    if (firstIntentCaptured) return;

    firstIntentCaptured = true;
    startLazyLoading();
  }

  function captureKeyboardIntent(event) {
    var keys = [
      'ArrowDown',
      'PageDown',
      ' ',
      'Spacebar',
      'End'
    ];

    if (keys.indexOf(event.key) !== -1) {
      captureIntent();
    }
  }

  function installIntentListeners() {
    if (!document.querySelector(SELECTOR)) return;

    window.addEventListener('wheel', captureIntent, { passive: true, once: true });
    window.addEventListener('touchstart', captureIntent, { passive: true, once: true });
    window.addEventListener('pointerdown', captureIntent, { passive: true, once: true });
    window.addEventListener('keydown', captureKeyboardIntent, { passive: true });
    window.addEventListener('scroll', captureIntent, { passive: true, once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installIntentListeners, { once: true });
  } else {
    installIntentListeners();
  }
})();
